<template>
  <v-dialog v-model="shown" :max-width="600" persistent>
    <v-card>
      <v-responsive>
        <v-card-title class="d-flex flex-wrap align-start align-sm-center px-6 pb-0 pt-6">
          <h3 class="mb-2 mb-sm-0 mr-2" style="word-break: keep-all !important; overflow-wrap: normal !important; white-space: normal !important; hyphens: none !important;">
            {{ $t( "signflow.AddNewSigner" ) }}
          </h3>
        </v-card-title>
        <v-card-text class="px-0 pt-4 pb-6">
          <v-card class="pa-2 mt-0 mb-0" color="#ffffff" flat>
            <v-form v-model="formValid" ref="form">
              <div v-if="showAddMeButton" class="mx-auto mt-0 mb-4" :style="{ width: $vuetify.breakpoint.xsOnly ? '100%' : '92%' }">
                <v-radio-group v-model="signerMode" row @change="onSignerModeChange" hide-details>
                  <v-radio :label="$t( 'signflow.AddOther' )" value="other" style="min-width: 50%;"></v-radio>
                  <v-radio :label="$t( 'signflow.AddMe' )" value="me"></v-radio>
                </v-radio-group>
              </div>
              <div>
                <v-text-field
                    class="shio-sign-card-input mx-auto"
                    v-model="newSigner.signerName"
                    :rules="[ validateFullName, validateSignerName ]"
                    :label="$t( 'signflow.SignersName' )"
                    :readonly="isMeSigner"
                    required
                ></v-text-field>
              </div>
              <div>
                <v-tooltip bottom nudge-top="35" :disabled="!isEmailDisabled || $vuetify.breakpoint.smAndDown">
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <v-text-field
                          class="shio-sign-card-input mx-auto"
                          v-model="newSigner.signerEmail"
                          :rules="[ validateEmail, signerIsNew ]"
                          :label="$t( 'signflow.SignersEmail' )"
                          :disabled="isEmailDisabled"
                          @input="checkSignerEmail"
                          :readonly="isMeSigner"
                          :hint="( isEmailDisabled && $vuetify.breakpoint.smAndDown ) ? $t( 'signflow.InsufficientFreeTrialInvitations' ) : ''"
                          :persistent-hint="isEmailDisabled && $vuetify.breakpoint.smAndDown"
                          required
                      ></v-text-field>
                    </div>
                  </template>
                  <span>{{ $t( "signflow.InsufficientFreeTrialInvitations" ) }}</span>
                </v-tooltip>
              </div>
              <div class="shio-sign-card-input language-select mx-auto" :class="{ shio_invisible_content : isMeSigner }">
                <v-select
                    v-model="newSigner.locale"
                    :label="$t('signedin.Locales')"
                    :items="language_options"
                    item-text="language"
                    item-value="locale"
                    :disabled="isMeSigner"
                ></v-select>
              </div>
              <div style="text-align: left" v-if="isAdvancedFlow" class="shio-sign-card-input mx-auto pt-5">
                <v-autocomplete
                    v-model="newSigner.allowedSignatureTypes"
                    :rules="[ validateAQTypes ]"
                    :items="localizedSignatureTypes"
                    item-text="text"
                    item-value="value"
                    dense
                    chips
                    small-chips
                    multiple
                    :disabled="isSignatureTypesDisabled"
                    :label="$t( 'signflow.AllowedSignatureTypes' )"
                ></v-autocomplete>
              </div>
              <p v-if="remainingSignersAllowed !== null" class="text-left pt-7" style="width: 80%; margin-left: 10%; color: #666666;">
                <v-icon style="font-size: 20px; vertical-align: text-bottom;" class="mr-1 alert-info-icon">mdi-alert-circle</v-icon>
                {{ $t('signflow.AddUpToXMoreSigners1') }}
                <strong>{{ remainingSignersAllowed }}</strong>
                {{ $t('signflow.AddUpToXMoreSigners2') }}
              </p>
            </v-form>
          </v-card>
        </v-card-text>
        <v-card-actions class="px-6 pb-4 pt-0">
          <v-btn
              @click="onCancel"
              :class="$vuetify.breakpoint.smAndUp ? 'secondary-btn' : ''"
              :style="$vuetify.breakpoint.smAndUp ? { width: '120px !important', minWidth: '120px !important', padding: '0 !important' } : {}"
          >
            {{ $t( "signflow.Cancel" ) }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
              @click="onAdd"
              color="primary"
              :class="$vuetify.breakpoint.smAndUp ? 'primary-btn' : ''"
              :disabled="!isValid"
              :loading="isSaving"
              :style="$vuetify.breakpoint.smAndUp ? { width: '120px !important', minWidth: '120px !important', padding: '0 !important' } : {}"
          >
            {{ $t( "signflow.Confirm" ) }}
          </v-btn>
        </v-card-actions>
      </v-responsive>
    </v-card>
  </v-dialog>
</template>

<script>
import util from "../../../../util/util";

const locales = [ "en", "fi", "sv", "pl", "de", "nl", "fr", "es", "ro", "th" ];
const AQSignaturePrice = process.env.VUE_APP_WALLET_PRICE ? JSON.parse( process.env.VUE_APP_WALLET_PRICE ) : [];

export default {
    name : "AddSignerDialog",
    props : {
        resolve : Function,
        flow : Object
    },
    data()
    {
        return {
            formValid : false,
            shown : false,
            signerMode: "other",
            newSigner : {
                signerName: "",
                signerEmail: "",
                locale: "",
                allowedSignatureTypes: []
            },
            otherSigners : [],
            orgUsers : [],
            isSaving : false,
            enabled_advanced_signature_types: []
        }
    },
    computed : {
        showAddMeButton()
        {
            return !this.meAlreadyAdded;
        },
        isMeSigner()
        {
            return this.newSigner.signerEmail.trim().toLowerCase() === $cc_user_info.email.trim().toLowerCase();
        },
        meAlreadyAdded()
        {
            return this.otherSigners.some( s => s.signerEmail.trim().toLowerCase() === $cc_user_info.email.trim().toLowerCase() );
        },
        localizedSignatureTypes()
        {
            let available_types = this.enabled_advanced_signature_types;
            if( this.flow && this.flow.documents && this.flow.documents.length > 1 )
            {
                available_types = available_types.filter( type => type !== "smart-id-signature" && type !== "certme-qes-signature" );
            }
            const existingTypes = this.otherSigners.reduce( ( acc, s ) => {
                if( s.allowedSignatureTypes )
                {
                    return acc.concat( s.allowedSignatureTypes );
                }
                return acc;
            }, [] ).filter( type => type !== "email-based-signature" );
            const hasFtn = existingTypes.includes( "fi-ftn-advanced-signature" );
            if( hasFtn )
            {
                available_types = available_types.filter( type => type === "fi-ftn-advanced-signature" );
            }
            else if( existingTypes.length > 0 )
            {
                available_types = available_types.filter( type => type !== "fi-ftn-advanced-signature" );
            }
            return available_types.map( signature_type => ( {
                value : signature_type,
                text : this.$t( "signedin.SignatureType!" + signature_type )
            } ) );
        },
        isSignatureTypesDisabled()
        {
            const availableValues = this.localizedSignatureTypes.map( t => t.value );
            return availableValues.length === 1 && availableValues[0] === 'fi-ftn-advanced-signature';
        },
        isEmailDisabled()
        {
            if( this.flow && this.flow.signatureLevel === 'advanced' )
            {
                return false;
            }
            const stripeOn = $cc_user_info.stripePaymentOn;
            const hasSub = $cc_user_info.subscription && $cc_user_info.subscription.has_valid_subscription;
            const invitesLeft = $cc_user_info.organization ? $cc_user_info.organization.free_basic_invitations : 0;
            if( !stripeOn || hasSub )
            {
                return false;
            }
            return invitesLeft <= 0;
        },
        isAdvancedFlow()
        {
            return this.flow && this.flow.signatureLevel === 'advanced';
        },
        language_options()
        {
            return locales.map( locale => ( {
                locale,
                language : this.$t( "signedin.locale!" + locale )
            } ) );
        },
        isValid()
        {
            const isBaseValid = this.newSigner.signerName.trim() !== ''
                                && this.newSigner.signerEmail.trim() !== ''
                                && this.formValid;
            const isUnderMaxSigners = this.flow && this.flow.maxSigners ? this.otherSigners.length < this.flow.maxSigners : true;
            if( !isUnderMaxSigners ) return false;
            if( this.isAdvancedFlow )
            {
                return isBaseValid && this.newSigner.allowedSignatureTypes.length > 0;
            }
            return isBaseValid;
        },
        signerIsNew()
        {
            return !this._signerExists( this.newSigner.signerEmail ) || this.$t( "message.MustBeUniqueEmail" );
        },
        walletEnabled()
        {
            return window.$cc_user_info.organization && window.$cc_user_info.organization.wallet_enabled;
        },
        estimatedCost()
        {
            if( this.isAdvancedFlow && this.newSigner.allowedSignatureTypes.length > 0 )
            {
                let highestPrice = 0;
                this.newSigner.allowedSignatureTypes.forEach( type =>
                {
                    if( AQSignaturePrice[ type ] > highestPrice )
                    {
                        highestPrice = AQSignaturePrice[ type ];
                    }
                } );
                return highestPrice;
            }
            return 0;
        },
        remainingSignersAllowed()
        {
            if ( this.flow && this.flow.maxSigners )
            {
                return this.flow.maxSigners - this.otherSigners.length;
            }
            return null;
        }
    },
    methods : {
        validateFullName : util.validateFullName,
        validateEmail : util.validateEmail,
        validateSignerName( value )
        {
            return( !value || util.goodRegExp.test( value ) || this.$t( "message.EnterValidCharacters" ) );
        },
        validateAQTypes( value )
        {
            return ( this.isAdvancedFlow && value && value.length > 0 ) || this.$t( "message.EnterAtLeastOneAQType" );
        },
        applyDefaultSignatureTypes()
        {
            if( !this.isAdvancedFlow )
            {
                this.newSigner.allowedSignatureTypes = [];
                return;
            }
            const existingTypes = this.otherSigners.reduce( ( acc, s ) => {
                if( s.allowedSignatureTypes )
                {
                    return acc.concat( s.allowedSignatureTypes );
                }
                return acc;
            }, [] ).filter( type => type !== "email-based-signature" );
            const uniqueExistingTypes = [ ...new Set( existingTypes ) ];
            const availableValues = this.localizedSignatureTypes.map( t => t.value );
            if( uniqueExistingTypes.length > 0 )
            {
                this.newSigner.allowedSignatureTypes = uniqueExistingTypes.filter( type => availableValues.includes( type ) );
            }
        },
        onCancel()
        {
            this.resolve( false );
        },
        onAdd()
        {
            if( !this.isValid ) return;
            this.isSaving = true;
            if( this.isAdvancedFlow && this.walletEnabled && this.estimatedCost > 0 )
            {
                const country = window.$cc_user_info.subscription && window.$cc_user_info.subscription.address ? window.$cc_user_info.subscription.address.country : null;
                util.checkWallet( this.$store.shioCoreClient, this.estimatedCost, country ).then( r =>
                {
                    if( r )
                    {
                        this.executeAddSigner();
                    }
                    else
                    {
                        this.isSaving = false;
                        this.$parent.$alert( {
                            text : this.$t( "wallet.Error!InsufficientWalletBalance" ),
                            acceptText : this.$t( "signflow.Ok" )
                        } );
                        this.resolve( false );
                    }
                } ).catch( err =>
                {
                    this.isSaving = false;
                    this.$parent.$error( this.$t( "signflow.Error!Unexpected" ) );
                    this.resolve( false );
                } );
            }
            else
            {
                this.executeAddSigner();
            }
        },
        executeAddSigner()
        {
            let payload = {};
            if( this.isMeSigner )
            {
                payload = {
                    signerType : "me",
                    locale : this.newSigner.locale,
                    allowedSignatureTypes : this.isAdvancedFlow ? this.newSigner.allowedSignatureTypes : ["email-based-signature"]
                };
            }
            else
            {
                payload = {
                    signerType : "others",
                    signerName : this.newSigner.signerName.trim(),
                    signerEmail : this.newSigner.signerEmail.trim().toLowerCase(),
                    locale : this.newSigner.locale,
                    allowedSignatureTypes : this.isAdvancedFlow ? this.newSigner.allowedSignatureTypes : ["email-based-signature"]
                };
            }
            this.$store.signFlowClient.createSignerForSignflow(
                this.flow.groupId,
                this.flow.uuid,
                payload
            )
                .then( () =>
                {
                    this.resolve( true );
                } )
                .catch( err =>
                {
                    let errorMsg = this.$t( "signflow.Error!Unexpected" );
                    if( err && typeof err === "string" )
                    {
                        errorMsg = err;
                    }
                    else if( err && err.response && err.response.data && err.response.data.error )
                    {
                        errorMsg = err.response.data.error;
                    }
                    else if( err && err.message )
                    {
                        errorMsg = err.message;
                    }
                    this.$parent.$error( errorMsg );
                    this.resolve( false );
                } )
                .finally( () =>
                {
                    this.isSaving = false;
                } );
        },
        checkSignerEmail()
        {
            if ( this.isMeSigner )
            {
                this.signerMode = "me";
            }
            else
            {
                this.signerMode = "other";
            }
            if( !this.newSigner.signerEmail ) return;
            const users = this.orgUsers.filter( user => user.email.trim().toLowerCase() === this.newSigner.signerEmail.trim().toLowerCase() );
            if( users.length )
            {
                this.newSigner.signerName = users[ 0 ].name;
            }
        },
        _signerExists( email )
        {
            if( !email ) return false;
            return this.otherSigners.some( s => s.signerEmail.trim().toLowerCase() === email.trim().toLowerCase() );
        },
        onSignerModeChange( mode )
        {
            if( mode === 'me' )
            {
                this.newSigner.signerName = $cc_user_info.name;
                this.newSigner.signerEmail = $cc_user_info.email;
            }
            else
            {
                this.newSigner.signerName = "";
                this.newSigner.signerEmail = "";
                if ( this.$refs.form )
                {
                    this.$nextTick( () => { this.$refs.form.resetValidation(); } );
                }
            }
        },
    },
    mounted()
    {
        this.shown = true;
        if( this.flow )
        {
            if( this.flow.signers )
            {
                this.otherSigners = this.flow.signers.map( signer =>
                {
                    const mappedSigner = { ...signer };
                    if( this.flow.signEvents )
                    {
                        const event = this.flow.signEvents.find( e => e.signerEmail === signer.signerEmail );
                        if( event && event.allowedSignatureTypes )
                        {
                            mappedSigner.allowedSignatureTypes = event.allowedSignatureTypes;
                        }
                    }
                    return mappedSigner;
                } );
            }
            this.newSigner.locale = ( this.flow.process && this.flow.process.locale ) || this.flow.locale || util.getLocale();
        }
        else
        {
            this.newSigner.locale = util.getLocale();
        }
        if( window.$cc_user_info.organization && window.$cc_user_info.organization.org_signature_types )
        {
            const signature_types = window.$cc_user_info.organization.org_signature_types.slice();
            let validTypes = signature_types.filter( item => item !== "email-based-signature" );
            if( this.flow && this.flow.documents && this.flow.documents.length > 1 )
            {
                validTypes = validTypes.filter( type => type !== "smart-id-signature" && type !== "certme-qes-signature" );
            }
            const existingTypes = this.otherSigners.reduce( ( acc, s ) =>
            {
                if( s.allowedSignatureTypes )
                {
                    return acc.concat( s.allowedSignatureTypes );
                }
                return acc;
            }, [] ).filter( type => type !== "email-based-signature" );
            if( existingTypes.length > 0 )
            {
                if( existingTypes.includes( "fi-ftn-advanced-signature" ) )
                {
                    validTypes = validTypes.filter( type => type === "fi-ftn-advanced-signature" );
                }
                else
                {
                    validTypes = validTypes.filter( type => type !== "fi-ftn-advanced-signature" );
                }
            }
            this.enabled_advanced_signature_types = validTypes;
            this.applyDefaultSignatureTypes();
        }
        this.$store.shioCoreClient.retrieveUsers( util.getOrganizationId() ).then( r =>
        {
            this.orgUsers = r.entities;
        } ).catch( err =>
        {
            console.error( "Failed to retrieve Organisation users: ", err );
        } );
    }
}
</script>

<style lang="sass" scoped>
@import "../../../../styles/style"

.theme--light.v-btn.v-btn--disabled
  background-color: #ff507a !important
  color: white !important
  opacity: .5

.shio-sign-card-input
  width: 90%
  margin: 0 auto

.shio_invisible_content
  visibility: hidden

.title-word-break
  word-break: normal !important
  overflow-wrap: normal !important
  white-space: normal !important

::v-deep .v-input--selection-controls__ripple
  transform: scale(0.7)

.alert-info-icon
  color: $dark-grey !important

// Mobile specific overrides
@media (max-width: 599px)
  .shio-sign-card-input
    width: 100% !important

</style>